import pexelsLogo from './pexels-logo.png'
import './SearchResults.css'
import Loader from 'react-loader-spinner'
import { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useKeyPress, useActiveElement, usePrevious } from '../util'
import { getByTopic } from '../home/actions'
import { search } from './actions'
import SearchResult from './SearchResult'

function PoweredByPexelsMessage() {
  return (
    <div className="powered-by-container">
      <img id="pexels-small-logo" src={pexelsLogo} alt="pexels Logo" />
      <p className="powered-by-text">{'Powered by Pexels'}</p>
    </div>
  )
}

function SearchResults({
  searchTerm,
  activeCategory,
  images,
  isSearching,
  showAttribution,
  totalImages,
}) {
  const [page, setPage] = useState(1)
  const imagesRef = useRef(null)
  const loadMoreRef = useRef(null)
  const lastRequestedPageRef = useRef(1)
  const [cursor, setCursor] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)
  const [insideImages, setInsideImages] = useState(false)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)
  const downPress = useKeyPress({
    targetKey: 'ArrowDown',
    ref: imagesRef,
    preventDefault: true,
  })
  const upPress = useKeyPress({
    targetKey: 'ArrowUp',
    ref: imagesRef,
    preventDefault: true,
  })
  const dispatch = useDispatch()
  const prevActiveCategory = usePrevious(activeCategory)
  const prevSearchTerm = usePrevious(searchTerm)
  const shouldLoadImages = images.length < totalImages
  const hasSearchLoadingState = typeof isSearching === 'boolean'
  const canLoadImages = shouldLoadImages && !isSearching
  const showLoader = shouldLoadImages && (!hasSearchLoadingState || isSearching)
  const focusedElement = useActiveElement()

  // due to browser default behavior of scrolling to tabbed items and the sticky covering the SearchResults, we need to rescroll back to top to view the results. Creates a disturbing jump
  useEffect(() => {
    if (focusedElement?.id === 'SearchResults') {
      const elPosition = focusedElement.getBoundingClientRect()
      // the magic number here belong to the height of the sticky search bar
      const topVisible = elPosition.top >= 115

      if (!topVisible) {
        window.scrollTo({
          behavior: 'smooth',
          top: 0,
        })
      }
    }
  }, [focusedElement])

  // checks if focus is on an element who has a data-id containing 'insideImagesContainer'
  // then sets the cursor to the index added to the data-id
  // full data-id looks like 'insideImagesContainer_[index]' (these exist on the image, set and remove background buttons)
  useEffect(() => {
    const dataIdContents =
      focusedElement?.getAttribute('data-id')?.split('_') || []
    if (dataIdContents[0] === 'insideImagesContainer') {
      setInsideImages(true)
      if (dataIdContents[1]) setCursor(Number(dataIdContents[1]))
    } else {
      // pause the count until we come back inside the container
      setInsideImages(false)
    }
  }, [focusedElement])

  // if inside the images container and downkey is pressed, shift the cursor location
  useEffect(() => {
    if (images.length && insideImages && downPress) {
      setHasUserInteracted(true)
      setCursor((prevState) => {
        if (prevState === null) return 0
        return prevState < images.length - 1 ? prevState + 1 : prevState
      })
    }
  }, [downPress, images, insideImages])

  // if inside the images container and upkey is pressed, shift the cursor location
  useEffect(() => {
    if (images.length && insideImages && upPress) {
      setHasUserInteracted(true)
      setCursor((prevState) => {
        if (prevState === null) return 0
        return prevState > 0 ? prevState - 1 : prevState
      })
    }
  }, [upPress, images, insideImages])

  // if the cursor position changes and we are inside the images container, set focus to that image
  useEffect(() => {
    if (images.length && insideImages) {
      setSelectedImage(images[cursor])
    }
  }, [cursor, images, insideImages])

  // When switching between two categories or two searches, reset pagination state.
  useEffect(() => {
    setPage(1)
    lastRequestedPageRef.current = 1
    setHasUserInteracted(false)
  }, [activeCategory, searchTerm])

  // Call corresponding API if we haven't reached the last page
  // Calls for the first page are handled at the parent component level
  useEffect(() => {
    if (canLoadImages && page > lastRequestedPageRef.current) {
      lastRequestedPageRef.current = page
      // Call topic based API if we're on the home page, otherwise use search API
      // Ensure that the same topic or search term used to load page 1's results
      // is also used for future api calls
      if (searchTerm === '' && prevActiveCategory === activeCategory) {
        dispatch(getByTopic({ query: activeCategory, page }))
      } else if (searchTerm !== '' && prevSearchTerm === searchTerm) {
        dispatch(search({ query: searchTerm, page }))
      }
    }
  }, [
    dispatch,
    prevActiveCategory,
    activeCategory,
    prevSearchTerm,
    searchTerm,
    page,
    canLoadImages,
  ])

  // Record real user intent before allowing automatic pagination.
  useEffect(() => {
    const markInteraction = () => {
      setHasUserInteracted(true)
    }

    const onScroll = () => {
      if (window.scrollY > 0) {
        markInteraction()
      }
    }

    const onKeyDown = (event) => {
      const scrollIntentKeys = [
        'ArrowDown',
        'ArrowUp',
        'PageDown',
        'PageUp',
        'End',
        'Home',
        ' ',
      ]
      if (scrollIntentKeys.includes(event.key)) {
        markInteraction()
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('wheel', markInteraction, { passive: true })
    window.addEventListener('touchmove', markInteraction, { passive: true })
    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('wheel', markInteraction)
      window.removeEventListener('touchmove', markInteraction)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  // Load one additional page when the bottom sentinel comes into view.
  useEffect(() => {
    if (!loadMoreRef.current || !hasUserInteracted || !canLoadImages) {
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry?.isIntersecting &&
          page + 1 > lastRequestedPageRef.current
        ) {
          setPage((currentPage) => currentPage + 1)
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 250px 0px',
        threshold: 0,
      }
    )

    observer.observe(loadMoreRef.current)

    return () => {
      observer.disconnect()
    }
  }, [canLoadImages, hasUserInteracted, page])

  return (
    <div className="search-results-container">
      <PoweredByPexelsMessage />
      <ul
        className="SearchResults"
        tabIndex="0"
        data-id="insideImagesContainer"
        ref={imagesRef}
        id="SearchResults"
        aria-label="Use the arrow keys to navigate between images"
      >
        {images
          .filter((image) => !!image)
          .map((image, index) => (
            <SearchResult
              index={index}
              key={'' + index + image.id}
              image={image}
              showAttribution={showAttribution}
              selectedImage={selectedImage}
            />
          ))}
      </ul>
      <div className={showLoader ? 'loader' : 'display-none'}>
        <Loader type="Circles" color="#d1d1d1" height={24} width={24} />
      </div>
      <div ref={loadMoreRef} aria-hidden="true" />
    </div>
  )
}

export default SearchResults
